import { TaskType } from "@/api/task.tsx"
import { User } from "@/api/user.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Command, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form.tsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.tsx"
import { Switch } from "@/components/ui/switch.tsx"
import { Textarea } from "@/components/ui/textarea.tsx"
import { formatTaskType } from "@/deduction_detail/Timeline2"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { get_display_name } from "@/utils"
import * as z from "zod"

const BACKUP_TYPES = [
  // Ops team:
  "Bill of lading (BOL)",
  "Proof of delivery (POD)",
  "Pick list",
  // Finance team:
  "Purchase Order (PO)",
  "Invoice",
  // Misc team / usually sales:
  "Email",
]

const MARGINWIZ_USER_ID = "a6ae6e82-69fb-47b8-8f9d-cdf76f825a0a"
const MARGINWIZ_ASSIGNEE_LABEL = "MarginWiz"

const ASSIGNABLE_TASK_TYPES: TaskType[] = Object.values(TaskType).filter(
  (t): t is TaskType => t !== TaskType.UpdateDcPo
)

const FORM_SCHEMA = z.object({
  user_ids: z.array(z.string()).min(1, { message: "Please select at least one team member" }),
  taskType: z.string().min(1, { message: "Please select a task type" }),
  backupType: z.array(z.string()).default([]),
  note: z.string().optional(),
  isTimeSensitive: z.boolean().default(false),
  // dueDate: z.date(),
})

interface AssignmentFormProps {
  users: User[]
  onSubmit: (values: any) => void
}

// Helper function to get task-specific note
function getTaskSpecificNote(taskType: TaskType): string {
  switch (taskType) {
    case TaskType.FileDispute:
      return "Please attach evidence and file a dispute if you agree this is invalid."
    case TaskType.PullBackup:
      return "Please upload the requested documents."
    case TaskType.Followup:
      return "Please follow up on this deduction."
    case TaskType.Validate:
      return "Please check if this is a valid deduction."
    case TaskType.MapAccountingCode:
      return "Please map the appropriate accounting code for this deduction."
    case TaskType.Expense:
      return "Please expense this deduction."
    default:
      return ""
  }
}

// Helper function to group users by team
function groupUsersByTeam(users: User[]): Record<string, User[]> {
  return users.reduce(
    (acc, user) => {
      const team = user.team || "Other"
      if (!acc[team]) {
        acc[team] = []
      }
      acc[team].push(user)
      return acc
    },
    {} as Record<string, User[]>
  )
}

// Helper function to sort users within a team by relevance to task type
function sortUsersByTaskRelevance(users: User[], taskType: TaskType): User[] {
  return users.sort((a, b) => {
    // Primary users first
    const aIsPrimary = a.is_primary_for && a.is_primary_for.length > 0
    const bIsPrimary = b.is_primary_for && b.is_primary_for.length > 0

    if (aIsPrimary && !bIsPrimary) return -1
    if (!aIsPrimary && bIsPrimary) return 1

    // Then by responsibilities relevance to task type
    const aRelevant = isUserRelevantForTask(a, taskType)
    const bRelevant = isUserRelevantForTask(b, taskType)

    if (aRelevant && !bRelevant) return -1
    if (!aRelevant && bRelevant) return 1

    // Finally by name
    return get_display_name(a).localeCompare(get_display_name(b))
  })
}

// Helper function to determine if user is relevant for a specific task type
function isUserRelevantForTask(user: User, taskType: TaskType): boolean {
  if (!user.responsibilities) return false

  switch (taskType) {
    case TaskType.PullBackup:
      return user.responsibilities.some(
        resp => resp.includes("BOL") || resp.includes("POD") || resp.includes("Purchase Order")
      )
    case TaskType.FileDispute:
      return user.responsibilities.some(resp => resp.includes("Invoice") || resp.includes("Trade promotion"))
    case TaskType.Validate:
      return user.responsibilities.some(resp => resp.includes("Invoice") || resp.includes("Contract"))
    case TaskType.MapAccountingCode:
      return user.responsibilities.some(resp => resp.includes("Invoice") || resp.includes("Purchase Order"))
    default:
      return false
  }
}

// Get ordered teams with "Other" at the end
function getOrderedTeams(teams: string[]): string[] {
  const otherTeams = teams.filter(team => team !== "Other")
  return [...otherTeams.sort(), "Other"]
}

function get_assignment_display_name(user: User): string {
  let name = get_display_name(user)

  // Add responsibilities info
  if (user.responsibilities && user.responsibilities.length > 0) {
    const primaryResponsibilities = user.is_primary_for || []
    const responsibilitiesText = user.responsibilities
      .map(resp => {
        const isPrimary = primaryResponsibilities.includes(resp)
        return isPrimary ? `⭐ ${resp}` : resp
      })
      .join(", ")
    name += ` - ${responsibilitiesText}`
  }

  return name
}

export function get_marginwiz_user_id(): string {
  return MARGINWIZ_USER_ID
}

export function AssignmentForm({ users, onSubmit }: AssignmentFormProps) {
  const form = useForm<z.infer<typeof FORM_SCHEMA>>({
    resolver: zodResolver(FORM_SCHEMA),
    defaultValues: {
      user_ids: [],
      taskType: "",
      backupType: [],
      note: "",
      isTimeSensitive: false,
    },
  })

  // Watch both taskType and backupType
  const taskType = form.watch("taskType")
  const backupType = form.watch("backupType")

  // Use effect to handle note updates
  useEffect(() => {
    if (taskType === TaskType.PullBackup) {
      if (Array.isArray(backupType) && backupType.length) {
        form.setValue("note", `${getTaskSpecificNote(TaskType.PullBackup)} Including: ${backupType.join(", ")}.`)
      } else {
        form.setValue("note", getTaskSpecificNote(TaskType.PullBackup))
      }
    } else if (taskType) {
      form.setValue("note", getTaskSpecificNote(taskType as TaskType))
    }
  }, [taskType, backupType])

  function handleSubmit(values: z.infer<typeof FORM_SCHEMA>) {
    // Concatenate backupType array into a comma-separated string for the backend
    const formattedValues = {
      ...values,
      backupType: Array.isArray(values.backupType) ? values.backupType.join(", ") : values.backupType,
    }
    onSubmit(formattedValues)
  }

  const groupedUsers = groupUsersByTeam(users)
  const orderedTeams = getOrderedTeams(Object.keys(groupedUsers))

  // Update note when task type changes
  function handleTaskTypeChange(value: string) {
    form.setValue("taskType", value)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 w-full max-w-md mx-auto">
        <FormField
          control={form.control}
          name="user_ids"
          render={({ field }) => {
            // Always ensure value is an array for robust production safety
            const selected: string[] = Array.isArray(field.value) ? field.value : []
            function toggle_user(user_id: string) {
              if (selected.includes(user_id)) {
                field.onChange(selected.filter((v: string) => v !== user_id))
              } else {
                field.onChange([...selected, user_id])
              }
            }
            function user_row(user_id: string, label: string) {
              return (
                <CommandItem key={user_id} value={label} onSelect={() => toggle_user(user_id)} className="flex items-center gap-2">
                  <div
                    className={`
                    h-4 w-4 shrink-0 rounded-sm border border-primary
                    ${selected.includes(user_id) ? "bg-primary" : "opacity-50"}
                  `}
                  />
                  <span>{label}</span>
                </CommandItem>
              )
            }
            return (
              <FormItem>
                <FormLabel>Team members</FormLabel>
                <Command className="border rounded-md">
                  <CommandInput placeholder="Search team members..." />
                  <div className="max-h-[300px] overflow-auto">
                    <CommandGroup heading="Special">{user_row(MARGINWIZ_USER_ID, MARGINWIZ_ASSIGNEE_LABEL)}</CommandGroup>
                    {orderedTeams.map(team => {
                      const teamUsers = groupedUsers[team]
                      if (!teamUsers?.length) return null

                      // Sort users by task relevance
                      const sortedUsers = sortUsersByTaskRelevance(teamUsers, taskType as TaskType)

                      return (
                        <CommandGroup key={team} heading={team}>
                          {sortedUsers.map(user => user_row(user.id, get_assignment_display_name(user)))}
                        </CommandGroup>
                      )
                    })}
                  </div>
                </Command>
                {selected.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    {selected.length} team member{selected.length === 1 ? "" : "s"} selected — each gets their own task
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )
          }}
        />

        <FormField
          control={form.control}
          name="taskType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Task Type</FormLabel>
              <Select onValueChange={handleTaskTypeChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a task type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ASSIGNABLE_TASK_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {formatTaskType(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {taskType === TaskType.PullBackup && (
          <FormField
            control={form.control}
            name="backupType"
            render={({ field }) => {
              // Always ensure value is an array for robust production safety
              const value: string[] = Array.isArray(field.value) ? field.value : []
              // This is a hardcoded constant, so no need for fallback
              return (
                <FormItem>
                  <FormLabel>Backup Types</FormLabel>
                  <Command className="border rounded-md">
                    <CommandInput placeholder="Search backup types..." />
                    {/* Intentionally removed <CommandEmpty> for production robustness */}
                    <CommandGroup className="max-h-[200px] overflow-auto">
                      {BACKUP_TYPES.map(type => (
                        <CommandItem
                          key={type}
                          onSelect={() => {
                            if (value.includes(type)) {
                              field.onChange(value.filter(v => v !== type))
                            } else {
                              field.onChange([...value, type])
                            }
                          }}
                          className="flex items-center gap-2">
                          <div
                            className={`
                            h-4 w-4 rounded-sm border border-primary
                            ${value.includes(type) ? "bg-primary" : "opacity-50"}
                          `}
                          />
                          <span>{type}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                  <FormMessage />
                </FormItem>
              )
            }}
          />
        )}

        {taskType && (
          <>
            {(taskType !== TaskType.PullBackup || backupType) && (
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Note</FormLabel>
                    <Textarea {...field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="isTimeSensitive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Time Sensitive</FormLabel>
                    <div className="text-xs text-muted-foreground">Mark this task as urgent for quick handling</div>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </>
        )}

        <Button type="submit" className="w-full" variant="tertiary">
          Submit
        </Button>
      </form>
    </Form>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("get_marginwiz_user_id", () => {
    it("returns the hardcoded MarginWiz assignee id", () => {
      expect(get_marginwiz_user_id()).toBe("a6ae6e82-69fb-47b8-8f9d-cdf76f825a0a")
    })
  })
}
